import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/api";
import { obrisiDokument, premjestiDokument, type DokumentDjelatnika } from "@/lib/dokumenti";
import { dohvatiRadnike, workerChoiceLabel, type Radnik } from "@/lib/radnici";

export function DocumentRemovalActions({ radnikId, documentId, onRemoved, onError }: {
  radnikId: string;
  documentId: string;
  onRemoved: (document: DokumentDjelatnika, moved: boolean) => void;
  onError: (message: string) => void;
}) {
  const [mode, setMode] = useState<"closed" | "choose" | "delete" | "move">("closed");
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [workers, setWorkers] = useState<Radnik[]>([]);
  const [selected, setSelected] = useState<Radnik | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "move") return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void dohvatiRadnike({ q: query.trim(), status: "svi", limit: 20 }, controller.signal)
        .then(page => { if (!controller.signal.aborted) { setWorkers(page.items.filter(worker => worker.id !== radnikId)); setSearchError(null); } })
        .catch((error: unknown) => { if (!controller.signal.aborted) setSearchError(errorMessage(error, "Pretraga radnika nije uspjela.")); });
    }, query ? 250 : 0);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [mode, query, radnikId]);

  async function submit() {
    if (busy || (mode === "delete" ? !reason.trim() : !selected?.id)) return;
    setBusy(true);
    try {
      const moved = mode === "move";
      const document = moved
        ? await premjestiDokument(radnikId, documentId, selected!.id!)
        : await obrisiDokument(radnikId, documentId, reason.trim());
      onRemoved(document, moved);
    } catch (error: unknown) {
      onError(errorMessage(error, "Dokument se ne može ukloniti s radnika."));
    } finally { setBusy(false); }
  }

  if (mode === "closed") return <Button className="min-h-11 shrink-0 self-start" variant="outline" onClick={() => setMode("choose")}>Ukloni s radnika</Button>;
  return <section aria-label="Ukloni s radnika" className="shrink-0 space-y-3 rounded border p-3">
    <div className="flex flex-wrap gap-2">
      <Button className="min-h-11" disabled={busy} variant={mode === "delete" ? "default" : "outline"} onClick={() => setMode("delete")}>Obriši</Button>
      <Button className="min-h-11 h-auto whitespace-normal" disabled={busy} variant={mode === "move" ? "default" : "outline"} onClick={() => setMode("move")}>Premjesti drugom radniku</Button>
      <Button className="min-h-11" disabled={busy} variant="outline" onClick={() => setMode("closed")}>Odustani</Button>
    </div>
    {mode === "delete" && <>
      <Label htmlFor="document-deletion-reason">Razlog brisanja (obvezno)</Label>
      <Input className="min-h-11" id="document-deletion-reason" required maxLength={1000} value={reason} disabled={busy} onChange={event => setReason(event.target.value)} />
      <p className="text-sm">Dokument ćete moći vratiti kroz „Prikaži obrisane”. Evidentirani podaci ostaju sačuvani.</p>
      <Button className="min-h-11" disabled={busy || !reason.trim()} onClick={() => void submit()}>{busy ? "Brisanje…" : "Potvrdi brisanje"}</Button>
    </>}
    {mode === "move" && <>
      <Label htmlFor="document-worker-search">Pretraži radnike</Label>
      <Input className="min-h-11" id="document-worker-search" value={query} disabled={busy} onChange={event => { setQuery(event.target.value); setSelected(null); setWorkers([]); }} />
      <div aria-label="Pronađeni radnici" className="max-h-48 space-y-1 overflow-auto">
        {workers.map(worker => <Button key={worker.id} className="min-h-11 h-auto w-full justify-start whitespace-normal text-left" variant={selected?.id === worker.id ? "default" : "outline"} disabled={busy} aria-pressed={selected?.id === worker.id} onClick={() => setSelected(worker)}>{workerChoiceLabel(worker)}</Button>)}
      </div>
      {searchError && <p role="alert">{searchError}</p>}
      {selected && <p>Premjesti na: {workerChoiceLabel(selected)}. Dokument će trebati ponovno provjeriti.</p>}
      <Button className="min-h-11" disabled={busy || !selected} onClick={() => void submit()}>{busy ? "Premještanje…" : "Potvrdi premještanje"}</Button>
    </>}
  </section>;
}
